// bluaIQ — TASK-WEB-16 (FASE 2 paso 3): client for the REAL "Cuantificar
// planos" flow against the Step 2 backend (upload → quantify → poll → payload).
//
// HONEST-LIFECYCLE RULES (Codex conditions):
// - The job is polled to a TERMINAL state (done | failed | partial) — and the
//   poll itself has a hard timeout that resolves to a clear, retryable state.
//   A spinner with no exit does not exist in this flow.
// - Worker refusals surface their EN/ES copy VERBATIM ("Nada fue inventado."
//   included) — the client never rewrites, translates or softens them.
// - Website-side launch failures (worker_not_configured, worker_crashed,
//   runner_timeout) are NEVER presented as worker findings; each gets its own
//   honest copy. worker_not_configured explicitly reassures that the uploaded
//   plan stayed saved and nothing was lost.
// - STALE-ID RULE (Codex hardening): quantify is requested either with NO id
//   (backend picks the latest non-superseded takeoff document) or with the id
//   returned by an upload in THIS UI session — never a remembered/older id.
//   The backend enforces the same rule server-side.
// - On `done`, the persisted payload is fetched and MUST pass the frontend
//   validatePayloadV3 gate before anything renders (same gate as the demo).
// - On `partial`, the banner + verbatim copy render; the client deliberately
//   does NOT fetch the project payload, because the Step 2 backend persists
//   payloads only for `done` runs — showing an older revision's payload under
//   a "partial" banner would misattribute stale data to the current plan.

import { apiFetch } from '../../utils/api'

export interface UploadedSourceDocument {
  id: string
  originalFilename: string | null
  sourceDocumentHash: string | null
  fileSizeBytes: number | null
  status: string
  uploadedAt: string
}

export interface QuantifyJobView {
  id: string
  status: string
  workerStatus: string | null
  workerCopy: { en: string | null; es: string | null } | null
  websiteError: string | null
  runId: string
  payloadId: string | null
}

export const JOB_POLL_INTERVAL_MS = 2_000
// Hard exit for the poll loop — generous vs the backend runner budget (180s),
// so a website-side hang still ends in a clear retryable state, never an
// endless spinner.
export const JOB_POLL_TIMEOUT_MS = 240_000

export const TERMINAL_JOB_STATUSES = ['done', 'failed', 'partial'] as const

export function shortHash(hash: string | null): string {
  return hash ? hash.slice(0, 12) : ''
}

/** TASK-WEB-22 (Codex ceremony blocker): apiFetch only rewrites the URL — it
 *  never attaches auth, and production requireAuth demands a Bearer token, so
 *  every real-flow call died with 401 BEFORE the takeoffOwner guard. All four
 *  calls now carry the Clerk token via the app's established header idiom. */
function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** A backend 401 in the real flow means the Clerk session expired — surfaced
 *  as its own code so the UI can show the friendly re-login card. */
export const SESSION_EXPIRED = 'session_expired'

export async function uploadSourceDocument(
  projectId: string,
  file: File,
  token: string | null,
): Promise<{ sourceDocument: UploadedSourceDocument; reused: boolean }> {
  const form = new FormData()
  form.append('file', file)
  // FormData body: ONLY the Authorization header — never a manual
  // Content-Type (the browser must set the multipart boundary itself).
  const res = await apiFetch(`/api/projects/${projectId}/takeoff/source-document`, {
    method: 'POST',
    headers: authHeaders(token),
    body: form,
  })
  if (res.status === 401) throw new Error(SESSION_EXPIRED)
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as { error?: string }).error ?? `upload_failed_http_${res.status}`)
  }
  return res.json()
}

/** Starts a quantify job. `sourceDocumentId` must be an id uploaded in THIS UI
 *  session (or omitted for the backend's latest-non-superseded default) — the
 *  stale-id rule above. */
export async function startQuantify(
  projectId: string,
  sourceDocumentId: string | undefined,
  token: string | null,
): Promise<{ jobId: string; status: string }> {
  const res = await apiFetch(`/api/projects/${projectId}/takeoff/quantify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(sourceDocumentId ? { sourceDocumentId } : {}),
  })
  if (res.status === 401) throw new Error(SESSION_EXPIRED)
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as { error?: string }).error ?? `quantify_failed_http_${res.status}`)
  }
  return res.json()
}

export async function getQuantifyJob(projectId: string, jobId: string, token: string | null): Promise<QuantifyJobView> {
  const res = await apiFetch(`/api/projects/${projectId}/takeoff/quantify/jobs/${jobId}`, {
    headers: authHeaders(token),
  })
  if (res.status === 401) throw new Error(SESSION_EXPIRED)
  if (!res.ok) throw new Error(`job_fetch_http_${res.status}`)
  const body = await res.json()
  return (body as { job: QuantifyJobView }).job
}

/** Identity of the source document behind a payload or a rejection — the
 *  name + fingerprint the UI must ALWAYS show so a rehydrated "previous
 *  result" is never confused with the just-attempted plan (TASK-WEB-33). */
export interface SourceDocumentRef {
  originalFilename: string | null
  sourceDocumentHash: string | null
}

/** The project's last SEALED terminal failure, persisted server-side
 *  alongside the jobs and exposed by the payload endpoint (TASK-WEB-33). */
export interface PersistedRejection {
  status: string
  workerStatus: string | null
  workerCopy: { en: string | null; es: string | null } | null
  finishedAt: string | null
  document: SourceDocumentRef | null
}

export async function getProjectPayload(projectId: string, token: string | null): Promise<{
  payload: unknown; sourceDocumentHash: string | null; readRunId: string | null; lineCount: number | null
  // TASK-WEB-36: server-computed sha256 of the persisted payload's canonical
  // JSON — the determinism-verification surface (distinct from the source PDF
  // hash). null when there is no persisted payload row.
  payloadSha256: string | null
  document: SourceDocumentRef | null; lastRejection: PersistedRejection | null
}> {
  const res = await apiFetch(`/api/projects/${projectId}/takeoff/quantify/payload`, {
    headers: authHeaders(token),
  })
  if (res.status === 401) throw new Error(SESSION_EXPIRED)
  if (!res.ok) throw new Error(`payload_fetch_http_${res.status}`)
  return res.json()
}

/** What mount-time hydration found for the project (TASK-WEB-33):
 *  - `rejection` — the LAST terminal attempt was a sealed failure. The UI must
 *    show the rejection card (with the rejected document's identity), and the
 *    previous successful payload — if any — is available ONLY through an
 *    explicit user selection, never auto-rendered.
 *  - `payload` — the last attempt succeeded (or the only persisted state is a
 *    payload): render it, with its document identity.
 *  - `none` — nothing persisted yet: the honest upload state. */
export type HydrationState =
  | { kind: 'rejection'; rejection: PersistedRejection; previousPayload: unknown | null; previousDocument: SourceDocumentRef | null }
  | { kind: 'payload'; payload: unknown; document: SourceDocumentRef | null; payloadSha256: string | null }
  | { kind: 'none' }

/** TASK-WEB-24 (Codex ceremony blocker #3) + TASK-WEB-33 (Codex NEEDS FIX):
 *  the quantification state must SURVIVE language switches, reloads and fresh
 *  visits — including a terminal REJECTION, which must not be silently
 *  replaced by an older document's successful payload. Returns `none` on 404
 *  or a transient fetch problem (the upload state is the honest fallback —
 *  nothing is lost server-side). A 401 still surfaces as session_expired. */
export async function hydratePersistedState(
  projectId: string,
  getToken: () => Promise<string | null>,
): Promise<HydrationState> {
  try {
    const { payload, document, lastRejection, payloadSha256 } = await getProjectPayload(projectId, await getToken())
    if (lastRejection) {
      return {
        kind: 'rejection',
        rejection: lastRejection,
        previousPayload: payload ?? null,
        previousDocument: document ?? null,
      }
    }
    if (payload) return { kind: 'payload', payload, document: document ?? null, payloadSha256: payloadSha256 ?? null }
    return { kind: 'none' }
  } catch (err) {
    if (err instanceof Error && err.message === SESSION_EXPIRED) throw err
    return { kind: 'none' }
  }
}

/** Builds the honest bilingual card for a PERSISTED rejection by reusing the
 *  same derivation the in-session terminal path uses — the sealed EN/ES copy
 *  stays VERBATIM across remounts, reloads and language switches. */
export function deriveRejectionCard(rejection: PersistedRejection): QuantifyCard | null {
  return deriveQuantifyCard({
    kind: 'terminal',
    job: {
      id: '', status: rejection.status, workerStatus: rejection.workerStatus,
      workerCopy: rejection.workerCopy, websiteError: null, runId: '', payloadId: null,
    },
  })
}

export type PollOutcome =
  | { kind: 'terminal'; job: QuantifyJobView }
  | { kind: 'timeout'; lastJob: QuantifyJobView | null }

/** Polls the job until it is terminal or the hard timeout elapses. Injectable
 *  clock/sleep so the full lifecycle is unit-testable without real waiting.
 *  TASK-WEB-22: `getToken` is called on EVERY tick — Clerk tokens are
 *  short-lived and a quantify run can outlast a single token. */
export async function pollJobUntilTerminal(
  projectId: string,
  jobId: string,
  options?: {
    getToken?: () => Promise<string | null>
    intervalMs?: number
    timeoutMs?: number
    onTick?: (job: QuantifyJobView) => void
    sleep?: (ms: number) => Promise<void>
    now?: () => number
  },
): Promise<PollOutcome> {
  const intervalMs = options?.intervalMs ?? JOB_POLL_INTERVAL_MS
  const timeoutMs = options?.timeoutMs ?? JOB_POLL_TIMEOUT_MS
  const sleep = options?.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const now = options?.now ?? Date.now
  const startedAt = now()
  let lastJob: QuantifyJobView | null = null
  for (;;) {
    const token = options?.getToken ? await options.getToken() : null
    lastJob = await getQuantifyJob(projectId, jobId, token)
    options?.onTick?.(lastJob)
    if ((TERMINAL_JOB_STATUSES as readonly string[]).includes(lastJob.status)) {
      return { kind: 'terminal', job: lastJob }
    }
    if (now() - startedAt >= timeoutMs) {
      return { kind: 'timeout', lastJob }
    }
    await sleep(intervalMs)
  }
}

// ── Terminal-state card model (pure — unit-tested) ───────────────────────────

export interface QuantifyCard {
  kind: 'worker_refusal' | 'website_error' | 'partial' | 'poll_timeout'
  /** Machine-readable cause: the verbatim sealed worker state, or the
   *  website-side error code. */
  code: string
  title: { en: string; es: string }
  /** For worker refusals/partial: the worker's VERBATIM copy. For website
   *  errors/timeout: honest website-side copy. */
  body: { en: string; es: string }
  retryable: boolean
}

const CARD_COPY = {
  workerRefusalTitle: {
    en: 'We could not complete the quantification',
    es: 'No pudimos completar la cuantificación',
  },
  partialTitle: { en: 'Partial reading available', es: 'Lectura parcial disponible' },
  workerNotConfigured: {
    title: {
      en: 'Quantification engine not available in this environment yet',
      es: 'El motor de cuantificación no está disponible en este entorno todavía',
    },
    body: {
      en: 'The quantification engine is not available in this environment yet — your plan was saved and nothing was lost.',
      es: 'El motor de cuantificación no está disponible en este entorno todavía — su plano quedó guardado y nada se perdió.',
    },
  },
  websiteError: {
    title: { en: 'The quantification could not run', es: 'La cuantificación no pudo ejecutarse' },
    body: {
      en: 'A problem on our side stopped this run — this is not a finding about your plan. Your plan stayed saved; you can try again.',
      es: 'Un problema de nuestro lado detuvo esta ejecución — no es un hallazgo sobre su plano. Su plano quedó guardado; puede intentar de nuevo.',
    },
  },
  pollTimeout: {
    title: { en: 'Still processing', es: 'Sigue en proceso' },
    body: {
      en: 'The quantification is taking longer than expected. Your plan stayed saved — you can check the status again.',
      es: 'La cuantificación está tardando más de lo esperado. Su plano quedó guardado — puede consultar el estado de nuevo.',
    },
  },
  // TASK-WEB-22 (Codex): a backend 401 mid-flow is an expired session — the
  // card must be friendly, never a technical code.
  sessionExpired: {
    title: { en: 'Your session expired', es: 'Su sesión expiró' },
    body: {
      en: 'Your session expired — please sign in again; your plan was not lost.',
      es: 'Su sesión expiró — vuelva a iniciar sesión; su plano no se perdió.',
    },
  },
} as const

/** Maps a terminal (or timed-out) job to its honest card. Returns null for
 *  `done` — the done path renders the validated payload, not a card. */
export function deriveQuantifyCard(outcome: PollOutcome): QuantifyCard | null {
  if (outcome.kind === 'timeout') {
    return {
      kind: 'poll_timeout',
      code: 'poll_timeout',
      title: CARD_COPY.pollTimeout.title,
      body: CARD_COPY.pollTimeout.body,
      retryable: true,
    }
  }
  const job = outcome.job
  if (job.status === 'done') return null
  if (job.workerStatus) {
    // Worker refusal or partial — VERBATIM passthrough of the sealed EN/ES
    // copy ("Nada fue inventado." included). Never rewritten client-side.
    return {
      kind: job.status === 'partial' ? 'partial' : 'worker_refusal',
      code: job.workerStatus,
      title: job.status === 'partial' ? CARD_COPY.partialTitle : CARD_COPY.workerRefusalTitle,
      body: { en: job.workerCopy?.en ?? '', es: job.workerCopy?.es ?? '' },
      retryable: false,
    }
  }
  // Website-side failure — honest, never dressed as a worker finding.
  const code = (job.websiteError ?? 'unknown_website_error').split(':')[0].trim()
  if (code === SESSION_EXPIRED) {
    return {
      kind: 'website_error',
      code,
      title: CARD_COPY.sessionExpired.title,
      body: CARD_COPY.sessionExpired.body,
      retryable: false,
    }
  }
  if (code === 'worker_not_configured') {
    return {
      kind: 'website_error',
      code,
      title: CARD_COPY.workerNotConfigured.title,
      body: CARD_COPY.workerNotConfigured.body,
      retryable: false,
    }
  }
  return {
    kind: 'website_error',
    code,
    title: CARD_COPY.websiteError.title,
    body: CARD_COPY.websiteError.body,
    retryable: true,
  }
}

// ── Excel export (owner-only). Server: GET .../takeoff/quantify/export.xlsx ──

/** Distinct reasons, one per HTTP status the endpoint can return. They are
 *  CODES, not copy: the component maps each to a bilingual sentence, so the
 *  transport layer never decides what the owner reads. */
export const EXPORT_ERR = {
  session: 'export_session_expired',   // 401 — token gone or expired
  notOwner: 'export_not_owner',        // 403 — authenticated, but not the owner
  noPayload: 'export_no_payload',      // 404 — no project, or nothing read yet
  contract: 'export_contract_failed',  // 422 — persisted payload no longer valid
  unknown: 'export_failed',            // anything else
} as const

/** Download the owner's Excel export for a project.
 *
 *  GET ONLY, and NO body: the document is built from what the pipeline already
 *  persisted and validated. The client cannot hand the server a payload to
 *  render — that guarantee is the endpoint's, and this helper must never grow a
 *  request body that would undermine it.
 *
 *  Returns the blob plus the server-chosen filename. The filename is read from
 *  Content-Disposition but re-sanitized here as well: the server builds it
 *  safely, and the browser is the second place a stray quote or path separator
 *  could do damage.
 */
/**
 * Ask the server whether THIS account may see the export button.
 *
 * Fails CLOSED on every abnormal outcome — non-200, malformed body, thrown
 * fetch. A UI exposure flag that defaults to "shown" when the server is
 * unreachable is not a flag; a network blip must never reveal the button.
 *
 * This is not a permission check. The export endpoint is owner-gated on its
 * own and does not consult the flag, so a `true` here grants nothing.
 */
export async function fetchTakeoffExcelExportAvailability(
  projectId: string,
  token: string | null,
): Promise<boolean> {
  try {
    const res = await apiFetch(
      `/api/projects/${projectId}/takeoff/quantify/export-availability`,
      { headers: authHeaders(token) },
    )
    if (!res.ok) return false
    const body: unknown = await res.json()
    return (body as { enabled?: unknown })?.enabled === true
  } catch {
    return false
  }
}

/** client_question card UI exposure — fail-closed exactly like the Excel
 *  availability check: any non-ok status or thrown error returns false, so the
 *  card never renders on a network hiccup, an expired session, or a non-owner
 *  403. A `true` here grants nothing; the card's safety is enforced inside the
 *  component, not by this flag. */
export async function fetchTakeoffClientQuestionAvailability(
  projectId: string,
  token: string | null,
): Promise<boolean> {
  try {
    const res = await apiFetch(
      `/api/projects/${projectId}/takeoff/quantify/client-question-availability`,
      { headers: authHeaders(token) },
    )
    if (!res.ok) return false
    const body: unknown = await res.json()
    return (body as { enabled?: unknown })?.enabled === true
  } catch {
    return false
  }
}

export async function downloadTakeoffExcel(
  projectId: string,
  lang: 'es' | 'en',
  token: string | null,
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(
    `/api/projects/${projectId}/takeoff/quantify/export.xlsx?lang=${lang}`,
    { headers: authHeaders(token) },
  )
  if (res.status === 401) throw new Error(EXPORT_ERR.session)
  if (res.status === 403) throw new Error(EXPORT_ERR.notOwner)
  if (res.status === 404) throw new Error(EXPORT_ERR.noPayload)
  if (res.status === 422) throw new Error(EXPORT_ERR.contract)
  if (!res.ok) throw new Error(EXPORT_ERR.unknown)

  const disposition = res.headers.get('Content-Disposition') ?? ''
  const raw = /filename="([^"]*)"/.exec(disposition)?.[1] ?? ''
  const filename = raw.replace(/[^A-Za-z0-9._-]/g, '') || `takeoff-${lang}.xlsx`
  return { blob: await res.blob(), filename }
}
